import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';
import _ from 'underscore';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BaseEditableField from 'ui-library/BaseEditableField';
import {EntityWithTextAndSuspended, MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {
  EntityInputWithSearch,
  EntityInputWithSearchProps,
} from 'ui-library/entity-input/EntityInputWithSearch';
import PartyList from 'ui-library/PartyList';
import PlaceholderText from 'ui-library/PlaceholderText';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

type LimitedEntityInputWithSearchProps = Omit<
  EntityInputWithSearchProps,
  'onEnter' | 'onChange' | 'value' | 'size' | 'emptyText' | 'shouldAllowMultiple' | 'autoFocus'
>;

interface EditableFieldProps {
  isDisabled: boolean;
  allowEmptyValue: boolean;
  dataTestAttrs?: DataTestAttrs;
  initialIsBeingEdited: boolean;
  shouldUseCompactDisplay?: boolean;
  chipSize?: TagsInputSize;
  isFullWidth?: boolean;
  noViewerPlaceholder?: boolean;
  customViewerValue?: string | JSX.Element;
}

interface ViewerProps {
  content: string | JSX.Element;
  compactDisplay: boolean;
}

interface PartiesWithAvatarViewerProps {
  parties: Array<MinimalEntity>;
  maxAvatars?: number;
  extraPopoverTextForPartyList?: string | null;
  popoverPrefixForPartyList?: string | null;
}

type PartyEditorValueProps =
  | {
      shouldAllowMultiple: true;
      value: Array<MinimalEntity>;
      onChange: (changedValue: Array<MinimalEntity>) => void;
    }
  | {
      shouldAllowMultiple?: false;
      value: MinimalEntity | null;
      onChange: (changedValue: MinimalEntity | null) => void;
    };

export type BaseProps = LimitedEntityInputWithSearchProps & EditableFieldProps & PartyEditorValueProps;

interface PartyFieldWithAvatarsDisplayProps {
  useAvatars: true;
  maxAvatars?: number;
  compactDisplay?: false;
  extraPopoverTextForPartyList?: string | null;
  popoverPrefixForPartyList?: string | null;
}

export type EditablePartyFieldProps =
  | (BaseProps & {useAvatars?: false})
  | (BaseProps & PartyFieldWithAvatarsDisplayProps);

const Viewer = (props: ViewerProps): JSX.Element => (
  <div
    className={classNames('u-ellipsis-text', {
      'editableField-text': !props.compactDisplay,
    })}
  >
    {props.content}
  </div>
);

const PartiesWithAvatarViewer = (props: PartiesWithAvatarViewerProps): JSX.Element => (
  <div className={css(styles.partyListWrapper)}>
    <PartyList
      parties={props.parties}
      maxParties={props.maxAvatars ?? props.parties.length}
      useAvatar={true}
      extraPopoverText={props.extraPopoverTextForPartyList}
      popoverPrefix={props.popoverPrefixForPartyList}
    />
  </div>
);

export default function EditablePartyField(props: EditablePartyFieldProps): JSX.Element {
  const normalizedValue = Array.isArray(props.value) ? props.value : props.value ? [props.value] : [];
  const [editingValue, setEditingValue] = useState<Array<MinimalEntity>>(normalizedValue);
  const [isBeingEdited, setIsBeingEdited] = useState(props.initialIsBeingEdited);
  const [hasChangedValue, setHasChangedValue] = useState(false);
  const compactDisplay = props.shouldUseCompactDisplay ?? false;

  const placeholderText = <PlaceholderText value={props.noViewerPlaceholder ? '' : 'None'} />;

  const placeholder = <Viewer content={placeholderText} compactDisplay={compactDisplay} />;
  const partiesViewer = props.useAvatars ? (
    <PartiesWithAvatarViewer
      parties={normalizedValue}
      maxAvatars={props.maxAvatars ?? normalizedValue.length}
      extraPopoverTextForPartyList={props.extraPopoverTextForPartyList}
      popoverPrefixForPartyList={props.popoverPrefixForPartyList}
    />
  ) : (
    <Viewer
      content={
        props.customViewerValue ?? normalizedValue.map((value) => value.name ?? value.handle).join(', ')
      }
      compactDisplay={compactDisplay}
    />
  );

  const shouldUseViewer = normalizedValue.length > 0;

  function handleChangeValue(entityArray: Array<EntityWithTextAndSuspended>): void {
    const minimalEntityArray: Array<MinimalEntity> = entityArray.map((entity) => ({
      id: entity.id,
      handle: entity.handle,
      name: entity.name,
    }));
    setEditingValue(minimalEntityArray);
    setHasChangedValue(true);
  }

  function handleComplete(): void {
    if (props.shouldAllowMultiple) {
      props.onChange(editingValue);
    } else {
      props.onChange(editingValue[0] ?? null);
    }
    setIsBeingEdited(false);
  }

  function handleEdit(): void {
    setIsBeingEdited(true);
    setHasChangedValue(false);
    setEditingValue(normalizedValue);
  }

  const isValueValid = (): boolean =>
    Boolean((props.allowEmptyValue || editingValue.length > 0) && hasChangedValue);

  function handleCancel(): void {
    setIsBeingEdited(false);
    setEditingValue(normalizedValue);
  }

  function handleEnter(): void {
    if (isValueValid()) {
      handleComplete();
    }
  }

  return (
    <BaseEditableField
      editor={
        <EntityInputWithSearch
          // eslint-disable-next-line no-restricted-syntax
          {..._.omit(
            props,
            'isDisabled',
            'shouldUseCompactDisplay',
            'dateTestAttrs',
            'initialIsBeingEdited',
            'value',
            'emptyValueAllowed'
          )}
          onEnter={handleEnter}
          onChange={handleChangeValue}
          value={editingValue}
          size={props.chipSize ?? TagsInputSize.LARGE}
          emptyText="No results found"
          autoFocus={true}
          className={classNames('u-flex-grow', props.className)}
        />
      }
      isFullWidth={props.isFullWidth}
      isBeingEdited={isBeingEdited}
      isCompleteEnabled={isValueValid()}
      isWritable={!props.isDisabled}
      viewer={shouldUseViewer ? partiesViewer : placeholder}
      onCancel={handleCancel}
      onComplete={handleComplete}
      onEdit={handleEdit}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}

const styles = StyleSheet.create({
  partyListWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: '5px',
    columnGap: '5px',
  },
});
